// Pure overlap detection for curvature-at-junction arcs. No OpenLayers imports —
// unit-tested with `node --test`. See arcOverlapGrouping.test.mjs.

// Two CaJ arcs describe the same junction movement when BOTH of their endpoints
// coincide. In real data an opposite-direction pair's endpoints are NOT bit-exact
// — each road encodes its junction "spot" slightly differently, so endpoints drift
// ~0.4-0.7m apart (measured). A genuinely distinct movement sits several meters
// away. So we group by actual distance, not by a fine grid: two arcs match when a
// pairing of their endpoints (same or swapped) keeps BOTH endpoints within this
// many meters of each other.
export const OVERLAP_MATCH_M = 2.5;

function dist(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

// Smallest "worst endpoint distance" over the two ways to pair i's and j's
// endpoints (same orientation vs. swapped). Arcs match when this is <= threshold.
function endpointMatchDistance(a, b) {
  const same = Math.max(dist(a.start, b.start), dist(a.end, b.end));
  const swapped = Math.max(dist(a.start, b.end), dist(a.end, b.start));
  return Math.min(same, swapped);
}

// The two physical junction nodes a group's arcs run between. All member endpoints
// cluster around two points; seed the clusters with the farthest-apart endpoint
// pair, assign each endpoint to the nearer seed, and average. Snapping arc endpoints
// to these shared nodes makes a pair actually meet (their raw endpoints drift apart).
function computeSharedNodes(members) {
  const pts = [];
  for (const m of members) {
    pts.push(m.start);
    pts.push(m.end);
  }
  let si = 0;
  let sj = 1;
  let best = -1;
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const d = dist(pts[i], pts[j]);
      if (d > best) {
        best = d;
        si = i;
        sj = j;
      }
    }
  }
  const seedA = pts[si];
  const seedB = pts[sj];
  const accA = [0, 0];
  const accB = [0, 0];
  let nA = 0;
  let nB = 0;
  for (const p of pts) {
    if (dist(p, seedA) <= dist(p, seedB)) {
      accA[0] += p[0];
      accA[1] += p[1];
      nA++;
    } else {
      accB[0] += p[0];
      accB[1] += p[1];
      nB++;
    }
  }
  const nodeA = nA ? [accA[0] / nA, accA[1] / nA] : seedA;
  const nodeB = nB ? [accB[0] / nB, accB[1] / nB] : seedB;
  return [nodeA, nodeB];
}

/**
 * Group arcs whose endpoints coincide (within OVERLAP_MATCH_M) and, for every member
 * of a group of size >= 2, return how to render it separated. Returns
 * Map<item.key, { offset, start, end }>:
 *   offset — signed unitless scalar; the style shifts the arc right-of-travel by
 *            `offset × px × resolution`, tapered to zero at the ends. Members are
 *            placed on symmetric slots about the centerline; the arc with the highest
 *            |curvature| (tightest radius) goes to the INSIDE of the bend. Inside (in
 *            the group's first member's right-of-travel frame) is +slot for a right
 *            turn, −slot for a left turn (`insideSign = −turnSign`). `slot × dir`
 *            (dir = travel vs. the first member) maps that to the per-arc scalar. With
 *            no turn/curvature info, members fall back to incoming order.
 *   start/end — the shared junction nodes this arc should be snapped to so the group
 *               actually meets at its endpoints. start is the node nearer the arc's
 *               own start; end is the other.
 * Arcs not in any multi-member group are absent. Grouping is transitive (union-find).
 */
export function groupOverlappingArcs(items) {
  const n = items.length;
  const parent = items.map((_, i) => i);
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (i, j) => {
    parent[find(i)] = find(j);
  };

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (endpointMatchDistance(items[i], items[j]) <= OVERLAP_MATCH_M) {
        union(i, j);
      }
    }
  }

  const groups = new Map();
  for (let i = 0; i < n; i++) {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(i);
  }

  const result = new Map();
  for (const indices of groups.values()) {
    if (indices.length < 2) continue;
    const members = indices.map((idx) => items[idx]);
    const [nodeA, nodeB] = computeSharedNodes(members);
    const ref = members[0];
    const size = members.length;

    // Slot each member symmetrically about the centerline. With a known bend, order
    // the slots by |curvature| so the tightest arc lands on the inside; otherwise
    // (straight/dummy or untagged) keep incoming order.
    const insideSign = ref.turnSign ? -ref.turnSign : 0;
    const ranked = members
      .map((m, i) => ({ i, c: m.absCurvature || 0 }))
      .sort((a, b) => a.c - b.c || a.i - b.i);
    const slot = new Array(size);
    ranked.forEach((entry, rank) => {
      slot[entry.i] = insideSign
        ? insideSign * (rank - (size - 1) / 2)
        : entry.i - (size - 1) / 2;
    });

    members.forEach((m, i) => {
      const dir = dist(m.start, ref.start) <= dist(m.start, ref.end) ? 1 : -1;
      const startNearA = dist(m.start, nodeA) <= dist(m.start, nodeB);
      result.set(m.key, {
        offset: slot[i] * dir,
        start: startNearA ? nodeA : nodeB,
        end: startNearA ? nodeB : nodeA,
      });
    });
  }
  return result;
}
